import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import useAuth from "../../../../hooks/useAuth";
import { apiVariable } from "../../../../api/apiVariable";
import GenericFormPopup from "../../../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import {
  createCandidateFieldConfiguration,
  createCandidateInitialValues,
} from "../../../../formConfigs/HireCRM/candidateFieldConfig";
import FileUploadField from "../../../General/FileUploadField/FileUploadField";

const CreateCandidatePopup = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const { token } = useAuth();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setCvFile(null);
    setCvError(null);
  };
  const handleSnackbarClose = () => setStatus("idle");

  const mutation = useMutation({
    mutationFn: (data: any) => {
      const formData = new FormData();
      formData.append("FirstName", data.firstName);
      formData.append("LastName", data.lastName);
      formData.append("PhoneNumber", data.phoneNumber);
      formData.append("Position", data.position);
      formData.append("Email", data.email);
      formData.append("Status", data.status);
      if (cvFile) {
        formData.append("Cv", cvFile);
      }
      return axios.post(`${apiVariable}/external-candidate`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["candidates"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to create candidate");
    },
  });

  const validateCv = (file: File | null): string | null => {
    if (!file) return "CV is required";
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext !== "pdf") return "Only PDF files are allowed";
    if (file.size > 5 * 1024 * 1024) return "File size exceeds limit (5MB)";
    return null;
  };

  const submitHandler = (data: any) => {
    const err = validateCv(cvFile);
    if (err) {
      setCvError(err);
      return;
    }
    mutation.mutate(data);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Candidate
      </Button>
      <GenericFormPopup
        initialValues={createCandidateInitialValues}
        formFields={createCandidateFieldConfiguration}
        title="Create New Candidate"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={mutation.isPending}
        additionalComponents={
          <FileUploadField
            label="Upload CV *"
            file={cvFile}
            onChange={(file) => {
              setCvFile(file);
              setCvError(null);
            }}
            error={cvError || undefined}
          />
        }
      />
      <StatusSnackbar
        status={status}
        successMessage="Candidate created successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default CreateCandidatePopup;
